/**
 * The landmark registry — issue #181.
 *
 * `greatTree.ts` and `mountain.ts` are the same shape drawn twice: one pure
 * oracle `at(wx, wy) -> material | none`, stamped by a generator pass in two
 * halves either side of `materialise` — a **kind** pass (so the block tags
 * and the connectivity verifier see the shape before the biome palette runs)
 * and a **dress** pass (the actual material, after painting). `greatTree.ts`'s
 * own header works out why that two-pass split is the right shape at all;
 * this file is what stops a third landmark from having to re-derive the loop
 * body by copying one of the first two.
 *
 * **The determinism rule carries over unchanged, and is structural here
 * rather than a comment to remember**: `at(wx, wy)` takes no seed and no
 * `Rng`. A landmark is the same landmark in every world — "a returning
 * player recognises it" (`greatTree.ts`) — and the `Landmark` interface
 * below simply has no parameter to violate that with.
 *
 * Adding a landmark: write an oracle module (`greatTree.ts` is the template —
 * `GREAT_TREE_BOUNDS`, a `chunkTouches*` guard, an `at` function, a `*_NONE`
 * sentinel), wrap it in a `Landmark` object here, and push it onto
 * `LANDMARKS`. `WorldGen.generateChunk`'s surface branch loops the registry
 * for both passes — no second call site to remember, no worldgen.ts edit.
 * Registration order is stamp order, both passes: a later landmark's cells
 * win where two overlap. (`descentShaft()` is not a `Landmark` — it is a
 * distinct, higher-priority mechanism with its own explicit ordering
 * documented at its own call site in `worldgen.ts`, and stays outside this
 * registry on purpose.)
 */

import { Flag, M, VOID } from '../sim/materials';
import { BLOCK_SHIFT } from './structure';
import { CHUNK_SHIFT, CHUNK_SIZE, type Chunk } from '../sim/world';
import { Kind } from './rooms';
import {
  GREAT_TREE_BOUNDS, TREE_NONE, chunkTouchesGreatTree, greatTreeAt,
} from './greatTree';
import { MOUNTAIN_BOUNDS, MOUNTAIN_NONE, chunkTouchesMountain, mountainAt } from './mountain';

export interface Landmark {
  readonly id: string;
  /** Cheap AABB check — skip a chunk the landmark's bounding box cannot touch at all. */
  touchesChunk(x0: number, y0: number, x1: number, y1: number): boolean;
  /** The one oracle. No seed, no `Rng` — see this file's own header. */
  at(wx: number, wy: number): number;
  /** This landmark's own "not here" sentinel (its oracle's return value for "absent"). */
  readonly none: number;
  /**
   * Extra per-cell kind-pass side effect, beyond writing `kind`/`protect` —
   * e.g. the Great Tree's hollow marking `pathBlocks` so the connectivity
   * verifier treats its interior as a guaranteed route, the way the ground
   * line already is. Called only for cells where `at` returned something
   * other than `none`. Most landmarks need nothing here.
   */
  onKindCell?(pathBlocks: Uint8Array, m: number, lx: number, ly: number): void;
}

export const GREAT_TREE_LANDMARK: Landmark = {
  id: 'great-tree',
  touchesChunk: chunkTouchesGreatTree,
  at: greatTreeAt,
  none: TREE_NONE,
  // The hollow is a route the landmark guarantees, so it outranks the block
  // heuristics in `annotate` the same way the ground line does — moved here
  // verbatim from the pre-registry `greatTreeKinds`.
  onKindCell(pathBlocks, m, lx, ly) {
    if (m !== VOID) return;
    pathBlocks[((ly >> BLOCK_SHIFT) << 4) | (lx >> BLOCK_SHIFT)] = 1;
  },
};

/**
 * `mountainAt` does *less* than `greatTreeAt` on purpose — the mountain's
 * bulk is already `Kind.SOLID` before this stamps anything, because
 * `surfaceCarve` filled every column under `surfaceHeight`, and
 * `surfaceHeight` is the mountain inside its own span. What this landmark
 * actually changes is the lip's overhang (solid rock where `surfaceCarve`
 * left sky — the only cell whose `kind` this landmark touches at all) and
 * `protect` (`docs/27` §7 item 4 — without it `deposits` and the pool are
 * free to dress an authored surface, and a tread that collects three cells
 * of spore drift is a tread whose height depends on the seed; `deposits`
 * deliberately ignores `protect` on its own, so this is the other half of
 * that belt-and-braces).
 */
export const MOUNTAIN_LANDMARK: Landmark = {
  id: 'mountain',
  touchesChunk: chunkTouchesMountain,
  at: mountainAt,
  none: MOUNTAIN_NONE,
};

/**
 * Stamp order for both passes. Tree before mountain reproduces the exact
 * call order the pre-registry code used (`greatTreeKinds` then
 * `mountainKinds`; `greatTreeDress` then `mountainDress`) — the two
 * landmarks' bounds do not overlap today, so this is a compatibility
 * guarantee rather than a live tie-break, but the ordering rule (registration
 * order = stamp order = last-write-wins) is the contract a future overlapping
 * landmark inherits.
 */
export const LANDMARKS: readonly Landmark[] = [GREAT_TREE_LANDMARK, MOUNTAIN_LANDMARK];

// Exported for tests and for anyone wiring a landmark's own bounds-derived
// tooling (e.g. a future world-map editor overlay) without reaching into
// `greatTree.ts`/`mountain.ts` directly.
export { GREAT_TREE_BOUNDS, MOUNTAIN_BOUNDS };

/**
 * The kind pass — shared body of what used to be `WorldGen.greatTreeKinds`
 * and `WorldGen.mountainKinds`. Writes `kind`/`protect` for every cell the
 * landmark's oracle claims, plus its optional `onKindCell` hook.
 */
export function stampLandmarkKinds(
  lm: Landmark,
  kind: Uint8Array,
  protect: Uint8Array,
  pathBlocks: Uint8Array,
  baseX: number,
  baseY: number,
): void {
  if (!lm.touchesChunk(baseX, baseY, baseX + CHUNK_SIZE - 1, baseY + CHUNK_SIZE - 1)) return;
  for (let ly = 0; ly < CHUNK_SIZE; ly++) {
    const wy = baseY + ly;
    const row = ly << CHUNK_SHIFT;
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      const m = lm.at(baseX + lx, wy);
      if (m === lm.none) continue;
      const i = row + lx;
      kind[i] = m === VOID ? Kind.OPEN : M.oneWay[m] !== 0 ? Kind.PLATFORM : Kind.SOLID;
      protect[i] = 1;
      lm.onKindCell?.(pathBlocks, m, lx, ly);
    }
  }
}

/**
 * The dress pass — shared body of what used to be `WorldGen.greatTreeDress`
 * and `WorldGen.mountainDress`. Runs immediately after `materialise`, over
 * the same oracle the kind pass used, so the two passes cannot describe two
 * different landmarks. `Flag.STATIC` on every placed cell: this is authored,
 * and a landmark that has slumped into a different shape by the time the
 * player walks up to it is not a landmark (the exception, standing water,
 * belongs to `pond`, not to this pass).
 */
export function stampLandmarkDress(
  lm: Landmark,
  chunk: Chunk,
  baseX: number,
  baseY: number,
): void {
  if (!lm.touchesChunk(baseX, baseY, baseX + CHUNK_SIZE - 1, baseY + CHUNK_SIZE - 1)) return;
  const { mat, temp, flags, aux } = chunk;
  for (let ly = 0; ly < CHUNK_SIZE; ly++) {
    const wy = baseY + ly;
    const row = ly << CHUNK_SHIFT;
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      const m = lm.at(baseX + lx, wy);
      if (m === lm.none) continue;
      const i = row + lx;
      mat[i] = m;
      // The variant bits `materialise` rolled are kept: they are a function
      // of the cell's world position, not of what is standing there, and
      // dropping them would give an authored trunk/cliff face a flat,
      // un-grained look.
      flags[i] = m === VOID ? flags[i] & ~Flag.STATIC : flags[i] | Flag.STATIC;
      const st = M.spawnTemp[m];
      if (m !== VOID && st !== 20) temp[i] = st;
      aux[i] = M.burnDuration[m];
    }
  }
}
